import { Col } from 'antd';
import { FieldValidator } from 'final-form';
import i18next from 'i18next';
import { omit } from 'lodash';
import { FC, FormEvent, FormEventHandler } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import Transition from 'react-transition-group/Transition';

import { ContentLocalesI } from '~src/interfaces/session.types';
import { KeyTextObj } from '~src/interfaces/translation.interface';
import { StoreI } from '~src/rootReducer';
import { getCompanyContentLocale } from '~src/services/session';
import CheckBox, { CheckBoxType } from '~src/shared/form/Checkbox';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import SelectInput from '~src/shared/form/select/SelectInput';
import * as SC from '~src/shared/form/translatableFormShared.sc';
import TileHeader from '~src/shared/headers/tileHeader/TileHeader';
import { FieldType } from '~src/types';

const duration = 200;

export interface FormTransValues {
  translations: ContentLocalesI[];
  isMultipleChoiceEnabled: boolean;
}

export type TranslatableDynamicFormFormData = FormTransValues & {
  selectedContentLocale: string;
  isTranslationBoxVisible: boolean;
};

interface FieldOptsI {
  label: string;
  labelLow?: string;
  labelHigh?: string;
  validator: FieldValidator<string>;
  translationValidator: FieldValidator<string>;
}

interface OwnProps {
  leftColSize: number;
  submitBtnKey?: string;
  cancelBtnKey?: string;
  loadingSave: boolean;
  titleKey?: string;
  handleSubmit: (event: FormEvent<any>) => Promise<any> | undefined;
  isMcq?: boolean;
  t: i18next.TFunction;
  form: string;
  fieldsOpts: FieldOptsI;
  tooltip?: KeyTextObj;
}

interface StateProps {
  defaultContentLocale: string;
  contentLocales: ContentLocalesI;
  filteredLocales: ContentLocalesI;
  isMcq?: boolean;
}

type Props = OwnProps & StateProps;

const TranslatableDynamicFormRaw: FC<Props> = (props) => {
  const formState = useFormState();
  const form = useForm();

  const getLowHighLabel = (index: number, arrLength: number) => {
    const { fieldsOpts } = props;
    if (fieldsOpts.labelHigh && fieldsOpts.labelLow) {
      if (arrLength === 1) {
        return fieldsOpts.labelLow;
      }
      switch (index) {
        case 0:
          return fieldsOpts.labelHigh;
        case arrLength - 1:
          return fieldsOpts.labelLow;
        default:
          return fieldsOpts.label;
      }
    }
    return fieldsOpts.labelHigh;
  };

  const renderTextInput = ({
    autoFocus,
    input,
    label,
    translationKey,
    meta: { touched, error },
  }: FieldType) => (
    <CustomFormInput
      {...input}
      autoFocus={autoFocus}
      error={touched ? error : null}
      label={label}
      type="text"
      labelKey={translationKey}
    />
  );

  const renderSelectInput = ({
    input,
    list,
    label,
    meta: { touched, error },
  }: FieldType & {
    list: ContentLocalesI;
  }) => {
    const optList = Object.entries(list).map(([key, value]) => ({ name: value, value: key }));
    return (
      <SelectInput
        {...input}
        t={props.t}
        label={label}
        labelKey={'BE'}
        optList={optList}
        error={touched ? error : null}
      />
    );
  };

  const renderCheckboxInput = ({ input, label }: FieldType) => {
    return (
      <CheckBox
        {...input}
        id={input.name}
        isChecked={input.value}
        type={CheckBoxType.CheckBox}
        label={label}
      />
    );
  };

  const renderArrFields = ({
    fields,
    data,
  }: FieldArrayRenderProps<FieldType, HTMLInputElement> & { data: { locale: string } }) => {
    const isTranslation = data.locale !== props.defaultContentLocale;
    return (
      <div>
        {fields.map((field, index) => {
          const labelKey = getLowHighLabel(index, fields.value.length) ?? props.fieldsOpts.label;
          return (
            <Field
              key={`${field}.${data.locale}`}
              name={`${field}.${data.locale}`}
              autoFocus={index === 0}
              label={props.t(labelKey)}
              children={renderTextInput}
              validate={props.fieldsOpts[isTranslation ? 'translationValidator' : 'validator'] || undefined}
              translationKey={labelKey}
            />
          );
        })}
      </div>
    );
  };

  const handleCancel = () => {
    form.setConfig('keepDirtyOnReinitialize', false);
    form.reset();
    form.setConfig('keepDirtyOnReinitialize', true);
  };

  const handleSubmit: FormEventHandler = (event) => {
    void props.handleSubmit(event)?.then((err) => {
      // reset the form when submission is successful so it is pristine again
      if (!err || !Object.keys(err).length) {
        handleCancel();
      }
    });
  };

  const { t, titleKey, filteredLocales, defaultContentLocale } = props;

  const cancelBtnKey = props.cancelBtnKey || 'x.shared.form.cancel_button';
  const submitBtnKey = props.submitBtnKey || 'x.shared.form.save_button';
  const leftColSize = props.leftColSize || 12;

  const onToggleTranslationBox = () => {
    form.change('isTranslationBoxVisible', !formState.values.isTranslationBoxVisible);
  };
  const showTranslationBox = formState.values.isTranslationBoxVisible;
  const areButtonsVisible = formState.dirty;
  return (
    <form onSubmit={handleSubmit} noValidate>
      {titleKey && <TileHeader title={t(titleKey)} titleKey={titleKey} tooltip={props.tooltip} border />}
      <SC.FormContent $inContainer={false}>
        <Transition in={showTranslationBox} timeout={duration}>
          {(status) => (
            <SC.FormContainer>
              <SC.DefaultContainer
                $leftColSize={leftColSize}
                $duration={duration}
                $toggled={showTranslationBox}
              >
                <SC.LeftColHeader>
                  <div>
                    <SC.DefLangTitle data-translation="x.shared.name_and_description_form.default_language">
                      {t('x.shared.name_and_description_form.default_language')}
                    </SC.DefLangTitle>
                    <SC.DefLangTxt>{props.contentLocales[defaultContentLocale]}</SC.DefLangTxt>
                  </div>
                  <SC.ToggleTranslationBtn onClick={onToggleTranslationBox}>
                    {!showTranslationBox && (
                      <span data-translation="x.shared.name_and_description_form.edit_translations">
                        {t('x.shared.name_and_description_form.edit_translations')}
                      </span>
                    )}
                  </SC.ToggleTranslationBtn>
                </SC.LeftColHeader>
                <SC.Fieldset $isCustomScaleForm>
                  <FieldArray
                    name="translations"
                    children={renderArrFields}
                    data={{ locale: defaultContentLocale }}
                  />
                </SC.Fieldset>
              </SC.DefaultContainer>
              <SC.TranslationContainer
                $leftColSize={leftColSize}
                $duration={duration}
                $status={status}
                $toggled={showTranslationBox}
              >
                {(status === 'entering' || status === 'exiting' || showTranslationBox) && (
                  <>
                    <SC.LeftColHeader>
                      <SC.ContentLocaleSelectBox>
                        <Field
                          name="selectedContentLocale"
                          label={t('x.users.content_language')}
                          children={renderSelectInput}
                          list={filteredLocales}
                        />
                      </SC.ContentLocaleSelectBox>
                      <SC.ToggleTranslationBtn onClick={onToggleTranslationBox}>
                        {showTranslationBox && (
                          <span data-translation="x.shared.name_and_description_form.hide_translations">
                            {t('x.shared.name_and_description_form.hide_translations')}
                          </span>
                        )}
                      </SC.ToggleTranslationBtn>
                    </SC.LeftColHeader>
                    <SC.Fieldset key={formState.values.selectedContentLocale} $isCustomScaleForm>
                      <FieldArray
                        name="translations"
                        children={renderArrFields}
                        data={{ locale: formState.values.selectedContentLocale }}
                      />
                    </SC.Fieldset>
                  </>
                )}
              </SC.TranslationContainer>
            </SC.FormContainer>
          )}
        </Transition>

        <SC.RowStyled>
          <Col>
            {props.isMcq && (
              <SC.CheckboxWrapper>
                <Field
                  name="isMultipleChoiceEnabled"
                  label={t('x.scale.type.mcq.type.label')}
                  children={renderCheckboxInput}
                  list={filteredLocales}
                />
              </SC.CheckboxWrapper>
            )}

            {areButtonsVisible ? (
              <SC.ButtonsPanel>
                <SC.CancelBtn type="button" onClick={handleCancel}>
                  <span data-translation={cancelBtnKey}>{t(cancelBtnKey)}</span>
                </SC.CancelBtn>
                <SC.SubmitBtn isLoading={props.loadingSave}>
                  <span data-translation={submitBtnKey}>{t(submitBtnKey)}</span>
                </SC.SubmitBtn>
              </SC.ButtonsPanel>
            ) : null}
          </Col>
        </SC.RowStyled>
      </SC.FormContent>
    </form>
  );
};

const mapStateToProps = (state: StoreI): StateProps => {
  const defaultContentLocale = getCompanyContentLocale(state);
  const { contentLocales } = state.user.session;
  const filteredLocales = omit(contentLocales, 'defaultContentLocale');

  return {
    defaultContentLocale,
    contentLocales,
    filteredLocales,
  };
};

export default connect(mapStateToProps)(TranslatableDynamicFormRaw);
